import { useMemo, useState, type ReactNode } from "react";
import { Card } from "../../../components/ui/Card";
import { SectionHeader } from "../../../components/ui/SectionHeader";
import { useAthleteProfile } from "../AthleteProfileProvider";
import { buildPowerZonesFromFtp } from "../seedAthleteProfile";
import type { AthleteProfile, MorningColdPlungeTiming } from "../types/athleteProfile";

type ValidationMap = Record<string, string>;

export type AthleteProfileEditorState = {
  draft: AthleteProfile;
  status: string;
  resetConfirmationOpen: boolean;
};

function splitCsv(value: string): string[] {
  return value
    .split(",")
    .map(item => item.trim())
    .filter(Boolean);
}

function toCsv(value: string[] | undefined): string {
  if (!value || value.length === 0) {
    return "";
  }

  return value.join(", ");
}

function toDateInputValue(value: string | null): string {
  return value ? value.slice(0, 10) : "";
}

function toIsoDate(value: string): string | null {
  if (!value) {
    return null;
  }

  const parsed = Date.parse(`${value}T00:00:00.000Z`);
  return Number.isNaN(parsed) ? null : new Date(parsed).toISOString();
}

function nowIso(): string {
  return new Date().toISOString();
}

export function validateAthleteProfile(profile: AthleteProfile): ValidationMap {
  const errors: ValidationMap = {};

  if (!profile.personal.firstName.trim()) {
    errors.firstName = "First name is required.";
  }

  if (profile.personal.heightInches <= 0) {
    errors.heightInches = "Height must be greater than zero.";
  }

  if (profile.personal.currentWeightLb <= 0) {
    errors.currentWeightLb = "Current weight must be greater than zero.";
  }

  if (profile.personal.targetWeightLb.minimum <= 0 || profile.personal.targetWeightLb.maximum <= 0) {
    errors.targetWeightLb = "Target weight range must be greater than zero.";
  }

  if (profile.personal.targetWeightLb.minimum > profile.personal.targetWeightLb.maximum) {
    errors.targetWeightLb = "Target weight minimum must be less than or equal to maximum.";
  }

  if (profile.performance.gravelFtpWatts !== null && profile.performance.gravelFtpWatts <= 0) {
    errors.gravelFtpWatts = "Gravel FTP must be greater than zero.";
  }

  if (profile.performance.indoorFtpWatts !== null && profile.performance.indoorFtpWatts <= 0) {
    errors.indoorFtpWatts = "Indoor FTP must be greater than zero.";
  }

  if (profile.performance.outdoorFtpWatts !== null && profile.performance.outdoorFtpWatts <= 0) {
    errors.outdoorFtpWatts = "Outdoor FTP must be greater than zero.";
  }

  if (profile.performance.ftpWatts <= 0) {
    errors.ftpWatts = "Current FTP must be greater than zero.";
  }

  if (profile.performance.defaultAnalysisFtpPolicy.kind === "manual" && (profile.performance.defaultAnalysisFtpPolicy.manualFtpWatts === null || profile.performance.defaultAnalysisFtpPolicy.manualFtpWatts <= 0)) {
    errors.defaultAnalysisFtpPolicy = "Manual default analysis FTP must be greater than zero.";
  }

  if (profile.performance.preferredCadenceRpm <= 0) {
    errors.preferredCadenceRpm = "Preferred cadence must be greater than zero.";
  }

  if (profile.performance.restingHeartRateBpm <= 0 || profile.performance.maxHeartRateBpm <= 0) {
    errors.heartRates = "Heart rate values must be greater than zero.";
  }

  if (!profile.goals.primaryEventName.trim()) {
    errors.primaryEventName = "Primary event name is required.";
  }

  if (profile.goals.primaryEventDate) {
    const date = new Date(profile.goals.primaryEventDate);
    if (Number.isNaN(date.getTime())) {
      errors.primaryEventDate = "Primary event date is invalid.";
    }
  }

  if (profile.nutrition.proteinTargetGrams <= 0 || profile.nutrition.defaultCarbohydrateTargetGrams <= 0 || profile.nutrition.defaultFatTargetGrams <= 0) {
    errors.nutritionTargets = "Nutrition targets must be greater than zero.";
  }

  if (profile.nutrition.dailyCalories <= 0 || profile.nutrition.dailySodiumTargetMg <= 0 || profile.nutrition.dailyFluidTargetMl <= 0) {
    errors.nutritionTargets = "Daily calories, sodium, and fluid targets must be greater than zero.";
  }

  if (profile.training.weeklyTrainingHours < 0 || profile.training.weeklyTssTarget < 0) {
    errors.trainingTargets = "Weekly training targets cannot be negative.";
  }

  if (profile.recovery.morningColdPlungeEnabled) {
    if (profile.recovery.morningColdPlungeDurationMinutes <= 0) {
      errors.morningColdPlungeDurationMinutes = "Cold plunge duration must be greater than zero.";
    }
  }

  return errors;
}

function cloneProfile(profile: AthleteProfile): AthleteProfile {
  return JSON.parse(JSON.stringify(profile)) as AthleteProfile;
}

export function createAthleteProfileEditorState(profile: AthleteProfile): AthleteProfileEditorState {
  return { draft: cloneProfile(profile), status: "", resetConfirmationOpen: false };
}

export function cancelAthleteProfileEdits(profile: AthleteProfile): AthleteProfileEditorState {
  return { draft: cloneProfile(profile), status: "Changes canceled.", resetConfirmationOpen: false };
}

export function requestAthleteProfileReset(state: AthleteProfileEditorState): AthleteProfileEditorState {
  return { ...state, resetConfirmationOpen: true, status: "" };
}

export function AthleteProfileSettingsPage() {
  const { athleteProfile, updateAthleteProfile, resetAthleteProfile } = useAthleteProfile();
  const [draft, setDraft] = useState<AthleteProfile>(() => cloneProfile(athleteProfile));
  const [errors, setErrors] = useState<ValidationMap>({});
  const [saveStatus, setSaveStatus] = useState<string>("");
  const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false);

  const hasUnsavedChanges = useMemo(() => JSON.stringify(draft) !== JSON.stringify(athleteProfile), [draft, athleteProfile]);

  function updateDraft(next: AthleteProfile) {
    setDraft(next);
    setSaveStatus("");
  }

  function handleSave() {
    const validation = validateAthleteProfile(draft);
    setErrors(validation);

    if (Object.keys(validation).length > 0) {
      setSaveStatus("Please resolve validation messages before saving.");
      return;
    }

    const nextProfile = cloneProfile(draft);
    const ftpChanged = nextProfile.performance.ftpWatts !== athleteProfile.performance.ftpWatts;
    if (ftpChanged) {
      nextProfile.performance.ftpEffectiveDateIso = nextProfile.performance.ftpEffectiveDateIso ?? nowIso();
      nextProfile.performance.ftpSource = "athlete profile";
      nextProfile.performance.ftpHistory = [
        ...(athleteProfile.performance.ftpHistory ?? []),
        {
          effectiveDateIso: nextProfile.performance.ftpEffectiveDateIso ?? nowIso(),
          ftpWatts: nextProfile.performance.ftpWatts,
          source: "athlete profile",
          policyKind: nextProfile.performance.defaultAnalysisFtpPolicy.kind,
          note: "Updated from Settings"
        }
      ];
      nextProfile.performance.powerZones = buildPowerZonesFromFtp(nextProfile.performance.ftpWatts);
    }

    updateAthleteProfile(nextProfile);
    setSaveStatus("Changes saved successfully.");
  }

  function handleCancel() {
    const canceled = cancelAthleteProfileEdits(athleteProfile);
    setDraft(canceled.draft);
    setErrors({});
    setSaveStatus(canceled.status);
    setResetConfirmationOpen(false);
  }

  function confirmReset() {
    const reset = resetAthleteProfile();
    setDraft(cloneProfile(reset));
    setErrors({});
    setSaveStatus("Profile reset to defaults.");
    setResetConfirmationOpen(false);
  }

  return (
    <div className="athlete-settings-layout">
      <Card>
        <SectionHeader
          eyebrow="PROFILE"
          title="Athlete Profile"
          subtitle="Local-first source of truth for athlete-specific values"
          action={<span className="ui-status-badge neutral">Draft {hasUnsavedChanges ? "Unsaved" : "Saved"}</span>}
        />

        <div className="athlete-form-actions" data-layout="persistent-actions">
          <button type="button" className="ride-analysis-button athlete-save-button primary-action" onClick={handleSave}>Save Changes</button>
          <button type="button" className="ride-analysis-button" onClick={handleCancel}>Cancel</button>
          <button type="button" className="ride-analysis-button danger-action" onClick={() => setResetConfirmationOpen(true)}>Reset to Defaults</button>
        </div>

        {saveStatus ? <p className="athlete-save-status" role="status">{saveStatus}</p> : null}
        {resetConfirmationOpen ? (
          <div className="athlete-reset-confirmation" role="alertdialog" aria-labelledby="reset-profile-title">
            <div>
              <strong id="reset-profile-title">Reset Athlete Profile?</strong>
              <p>This replaces all saved profile values with the seeded defaults.</p>
            </div>
            <div className="athlete-reset-actions">
              <button type="button" className="ride-analysis-button" onClick={() => setResetConfirmationOpen(false)}>Keep Current Profile</button>
              <button type="button" className="ride-analysis-button garage-delete-button danger-action" onClick={confirmReset}>Confirm Reset</button>
            </div>
          </div>
        ) : null}

        <FormSection title="Personal">
          <label>First Name<input value={draft.personal.firstName} onChange={event => updateDraft({ ...draft, personal: { ...draft.personal, firstName: event.target.value } })} /></label>
          <label>Last Name<input value={draft.personal.lastName} onChange={event => updateDraft({ ...draft, personal: { ...draft.personal, lastName: event.target.value } })} /></label>
          <label>Date of Birth<input type="date" value={draft.personal.dateOfBirth ?? ""} onChange={event => updateDraft({ ...draft, personal: { ...draft.personal, dateOfBirth: event.target.value || null } })} /></label>
          <label>Height (inches)<input type="number" value={draft.personal.heightInches} onChange={event => updateDraft({ ...draft, personal: { ...draft.personal, heightInches: Number(event.target.value) } })} /></label>
          <label>Current Weight (lb)<input type="number" step="0.1" value={draft.personal.currentWeightLb} onChange={event => updateDraft({ ...draft, personal: { ...draft.personal, currentWeightLb: Number(event.target.value) } })} /></label>
          <label>Target Weight Min (lb)<input type="number" step="0.1" value={draft.personal.targetWeightLb.minimum} onChange={event => updateDraft({ ...draft, personal: { ...draft.personal, targetWeightLb: { ...draft.personal.targetWeightLb, minimum: Number(event.target.value) } } })} /></label>
          <label>Target Weight Max (lb)<input type="number" step="0.1" value={draft.personal.targetWeightLb.maximum} onChange={event => updateDraft({ ...draft, personal: { ...draft.personal, targetWeightLb: { ...draft.personal.targetWeightLb, maximum: Number(event.target.value) } } })} /></label>
          <label>Preferred Units
            <select value={draft.personal.preferredUnits} onChange={event => updateDraft({ ...draft, personal: { ...draft.personal, preferredUnits: event.target.value as AthleteProfile["personal"]["preferredUnits"] } })}>
              <option value="imperial">Imperial</option>
              <option value="metric">Metric</option>
            </select>
          </label>
          <ValidationMessage message={errors.firstName ?? errors.heightInches ?? errors.currentWeightLb ?? errors.targetWeightLb} />
        </FormSection>

        <FormSection title="FTP">
          <label>Current FTP (W)<input type="number" value={draft.performance.ftpWatts} onChange={event => updateDraft({ ...draft, performance: { ...draft.performance, ftpWatts: Number(event.target.value) } })} /></label>
          <label>Effective Date<input type="date" value={toDateInputValue(draft.performance.ftpEffectiveDateIso)} onChange={event => updateDraft({ ...draft, performance: { ...draft.performance, ftpEffectiveDateIso: toIsoDate(event.target.value) } })} /></label>
          <label>FTP Source
            <select value={draft.performance.ftpSource} onChange={event => updateDraft({ ...draft, performance: { ...draft.performance, ftpSource: event.target.value as AthleteProfile["performance"]["ftpSource"] } })}>
              <option value="athlete profile">Athlete profile</option>
              <option value="manual entry">Manual entry</option>
              <option value="legacy settings">Legacy settings</option>
            </select>
          </label>
          <label>Indoor FTP (W)<input type="number" value={draft.performance.indoorFtpWatts ?? ""} onChange={event => updateDraft({ ...draft, performance: { ...draft.performance, indoorFtpWatts: event.target.value === "" ? null : Number(event.target.value) } })} /></label>
          <label>Outdoor FTP (W)<input type="number" value={draft.performance.outdoorFtpWatts ?? ""} onChange={event => updateDraft({ ...draft, performance: { ...draft.performance, outdoorFtpWatts: event.target.value === "" ? null : Number(event.target.value) } })} /></label>
          <label>Road FTP (W)<input type="number" value={draft.performance.roadFtpWatts ?? ""} onChange={event => updateDraft({ ...draft, performance: { ...draft.performance, roadFtpWatts: event.target.value === "" ? null : Number(event.target.value) } })} /></label>
          <label>Gravel FTP (W)<input type="number" value={draft.performance.gravelFtpWatts ?? ""} onChange={event => updateDraft({ ...draft, performance: { ...draft.performance, gravelFtpWatts: event.target.value === "" ? null : Number(event.target.value) } })} /></label>
          <label className="athlete-form-span-all">Default Analysis Policy
            <select
              value={draft.performance.defaultAnalysisFtpPolicy.kind}
              onChange={event => updateDraft({
                ...draft,
                performance: {
                  ...draft.performance,
                  defaultAnalysisFtpPolicy: {
                    kind: event.target.value as AthleteProfile["performance"]["defaultAnalysisFtpPolicy"]["kind"],
                    manualFtpWatts: draft.performance.defaultAnalysisFtpPolicy.manualFtpWatts
                  }
                }
              })}
            >
              <option value="athlete-profile-default">Athlete profile default analysis FTP</option>
              <option value="indoor">Indoor FTP</option>
              <option value="outdoor">Outdoor FTP</option>
              <option value="road">Road FTP</option>
              <option value="gravel">Gravel FTP</option>
              <option value="manual">Manual FTP</option>
            </select>
          </label>
          {draft.performance.defaultAnalysisFtpPolicy.kind === "manual" ? (
            <label>Manual Default FTP (W)<input type="number" value={draft.performance.defaultAnalysisFtpPolicy.manualFtpWatts ?? ""} onChange={event => updateDraft({ ...draft, performance: { ...draft.performance, defaultAnalysisFtpPolicy: { ...draft.performance.defaultAnalysisFtpPolicy, manualFtpWatts: event.target.value === "" ? null : Number(event.target.value) } } })} /></label>
          ) : null}
          <label>Max Heart Rate (bpm)<input type="number" value={draft.performance.maxHeartRateBpm} onChange={event => updateDraft({ ...draft, performance: { ...draft.performance, maxHeartRateBpm: Number(event.target.value) } })} /></label>
          <label>Resting Heart Rate (bpm)<input type="number" value={draft.performance.restingHeartRateBpm} onChange={event => updateDraft({ ...draft, performance: { ...draft.performance, restingHeartRateBpm: Number(event.target.value) } })} /></label>
          <label>Threshold Heart Rate (bpm)<input type="number" value={draft.performance.thresholdHeartRateBpm} onChange={event => updateDraft({ ...draft, performance: { ...draft.performance, thresholdHeartRateBpm: Number(event.target.value) } })} /></label>
          <ZoneEditor
            title="Power Zones"
            zones={draft.performance.powerZones}
            unit="W"
            onChange={powerZones => updateDraft({ ...draft, performance: { ...draft.performance, powerZones } })}
          />
          <HeartRateZoneEditor
            zones={draft.performance.heartRateZones}
            onChange={heartRateZones => updateDraft({ ...draft, performance: { ...draft.performance, heartRateZones } })}
          />
          <ValidationMessage message={errors.ftpWatts ?? errors.indoorFtpWatts ?? errors.outdoorFtpWatts ?? errors.gravelFtpWatts ?? errors.defaultAnalysisFtpPolicy ?? errors.preferredCadenceRpm ?? errors.heartRates} />
        </FormSection>

        <FormSection title="FTP History">
          <label className="athlete-form-span-all">Current FTP history<textarea rows={4} value={draft.performance.ftpHistory.map(entry => `${entry.effectiveDateIso} · ${entry.ftpWatts} W · ${entry.source} · ${entry.policyKind}${entry.note ? ` · ${entry.note}` : ""}`).join("\n")} readOnly /></label>
        </FormSection>

        <FormSection title="Goals">
          <label>Primary Objective<input value={draft.goals.primaryObjective} onChange={event => updateDraft({ ...draft, goals: { ...draft.goals, primaryObjective: event.target.value } })} /></label>
          <label>Primary Event Name<input value={draft.goals.primaryEventName} onChange={event => updateDraft({ ...draft, goals: { ...draft.goals, primaryEventName: event.target.value } })} /></label>
          <label>Primary Event Date<input type="date" value={draft.goals.primaryEventDate ?? ""} onChange={event => updateDraft({ ...draft, goals: { ...draft.goals, primaryEventDate: event.target.value || null } })} /></label>
          <label>Event Priority
            <select value={draft.goals.eventPriority} onChange={event => updateDraft({ ...draft, goals: { ...draft.goals, eventPriority: event.target.value as AthleteProfile["goals"]["eventPriority"] } })}>
              <option value="a">A</option>
              <option value="b">B</option>
              <option value="c">C</option>
            </select>
          </label>
          <label>Target Finish Goal<input value={draft.goals.targetFinishGoal} onChange={event => updateDraft({ ...draft, goals: { ...draft.goals, targetFinishGoal: event.target.value } })} /></label>
          <label>Body Composition Goal<input value={draft.goals.bodyCompositionGoal} onChange={event => updateDraft({ ...draft, goals: { ...draft.goals, bodyCompositionGoal: event.target.value } })} /></label>
          <ValidationMessage message={errors.primaryEventName ?? errors.primaryEventDate} />
        </FormSection>

        <FormSection title="Nutrition">
          <label>Daily Calories<input type="number" value={draft.nutrition.dailyCalories} onChange={event => updateDraft({ ...draft, nutrition: { ...draft.nutrition, dailyCalories: Number(event.target.value) } })} /></label>
          <label>Protein Target (g)<input type="number" value={draft.nutrition.proteinTargetGrams} onChange={event => updateDraft({ ...draft, nutrition: { ...draft.nutrition, proteinTargetGrams: Number(event.target.value) } })} /></label>
          <label>Default Carbohydrate Target (g)<input type="number" value={draft.nutrition.defaultCarbohydrateTargetGrams} onChange={event => updateDraft({ ...draft, nutrition: { ...draft.nutrition, defaultCarbohydrateTargetGrams: Number(event.target.value) } })} /></label>
          <label>Default Fat Target (g)<input type="number" value={draft.nutrition.defaultFatTargetGrams} onChange={event => updateDraft({ ...draft, nutrition: { ...draft.nutrition, defaultFatTargetGrams: Number(event.target.value) } })} /></label>
          <label>Daily Sodium Target (mg)<input type="number" value={draft.nutrition.dailySodiumTargetMg} onChange={event => updateDraft({ ...draft, nutrition: { ...draft.nutrition, dailySodiumTargetMg: Number(event.target.value) } })} /></label>
          <label>Daily Fluid Target (ml)<input type="number" value={draft.nutrition.dailyFluidTargetMl} onChange={event => updateDraft({ ...draft, nutrition: { ...draft.nutrition, dailyFluidTargetMl: Number(event.target.value) } })} /></label>
          <label>Preferred Ride Carbs per Hour (g)<input type="number" value={draft.nutrition.preferredRideCarbsPerHour} onChange={event => updateDraft({ ...draft, nutrition: { ...draft.nutrition, preferredRideCarbsPerHour: Number(event.target.value) } })} /></label>
          <label>Preferred Sodium per Hour (mg)<input type="number" value={draft.nutrition.preferredSodiumMgPerHour} onChange={event => updateDraft({ ...draft, nutrition: { ...draft.nutrition, preferredSodiumMgPerHour: Number(event.target.value) } })} /></label>
          <label>Preferred Fluids per Hour (ml)<input type="number" value={draft.nutrition.preferredFluidsMlPerHour} onChange={event => updateDraft({ ...draft, nutrition: { ...draft.nutrition, preferredFluidsMlPerHour: Number(event.target.value) } })} /></label>
          <label className="athlete-form-span-all">Nutrition Notes<textarea rows={3} value={draft.nutrition.nutritionNotes} onChange={event => updateDraft({ ...draft, nutrition: { ...draft.nutrition, nutritionNotes: event.target.value } })} /></label>
          <ValidationMessage message={errors.nutritionTargets} />
        </FormSection>

        <FormSection title="Recovery">
          <label>Preferred Sleep Hours<input type="number" step="0.1" value={draft.recovery.preferredSleepHours} onChange={event => updateDraft({ ...draft, recovery: { ...draft.recovery, preferredSleepHours: Number(event.target.value) } })} /></label>
          <label>Morning Cold Plunge Enabled
            <select value={draft.recovery.morningColdPlungeEnabled ? "yes" : "no"} onChange={event => updateDraft({ ...draft, recovery: { ...draft.recovery, morningColdPlungeEnabled: event.target.value === "yes" } })}>
              <option value="yes">Enabled</option>
              <option value="no">Disabled</option>
            </select>
          </label>
          <label>Cold Plunge Temperature (F)<input type="number" value={draft.recovery.morningColdPlungeTemperatureF} onChange={event => updateDraft({ ...draft, recovery: { ...draft.recovery, morningColdPlungeTemperatureF: Number(event.target.value) } })} /></label>
          <label>Cold Plunge Duration (minutes)<input type="number" value={draft.recovery.morningColdPlungeDurationMinutes} onChange={event => updateDraft({ ...draft, recovery: { ...draft.recovery, morningColdPlungeDurationMinutes: Number(event.target.value) } })} /></label>
          <label>Cold Plunge Timing
            <select value={draft.recovery.morningColdPlungeTiming} onChange={event => updateDraft({ ...draft, recovery: { ...draft.recovery, morningColdPlungeTiming: event.target.value as MorningColdPlungeTiming } })}>
              <option value="immediately-after-waking">Immediately after waking</option>
              <option value="pre-training">Pre-training</option>
              <option value="post-training">Post-training</option>
              <option value="evening">Evening</option>
            </select>
          </label>
          <label>Contrast Therapy Enabled
            <select value={draft.recovery.contrastTherapyEnabled ? "yes" : "no"} onChange={event => updateDraft({ ...draft, recovery: { ...draft.recovery, contrastTherapyEnabled: event.target.value === "yes" } })}>
              <option value="yes">Enabled</option>
              <option value="no">Disabled</option>
            </select>
          </label>
          <label className="athlete-form-span-all">Recovery Notes<textarea rows={3} value={draft.recovery.recoveryNotes} onChange={event => updateDraft({ ...draft, recovery: { ...draft.recovery, recoveryNotes: event.target.value } })} /></label>
          <ValidationMessage message={errors.morningColdPlungeDurationMinutes} />
        </FormSection>

        <FormSection title="Medical and Considerations" className="athlete-medical-section">
          <label className="athlete-form-span-all">Active Medications (comma-separated)<input value={toCsv(draft.medicalAndConsiderations.currentMedications)} onChange={event => updateDraft({ ...draft, medicalAndConsiderations: { ...draft.medicalAndConsiderations, currentMedications: splitCsv(event.target.value) } })} /></label>
          <label className="athlete-form-span-all">Injury History<textarea rows={3} value={draft.medicalAndConsiderations.injuryHistory ?? ""} onChange={event => updateDraft({ ...draft, medicalAndConsiderations: { ...draft.medicalAndConsiderations, injuryHistory: event.target.value } })} /></label>
          <label className="athlete-form-span-all">Recovery Considerations<textarea rows={3} value={draft.medicalAndConsiderations.recoveryConsiderations ?? ""} onChange={event => updateDraft({ ...draft, medicalAndConsiderations: { ...draft.medicalAndConsiderations, recoveryConsiderations: event.target.value } })} /></label>
          <label className="athlete-form-span-all">Medical Notes<textarea rows={3} value={draft.medicalAndConsiderations.medicalNotes ?? ""} onChange={event => updateDraft({ ...draft, medicalAndConsiderations: { ...draft.medicalAndConsiderations, medicalNotes: event.target.value } })} /></label>
        </FormSection>
      </Card>
    </div>
  );
}

function FormSection({ title, children, className = "" }: { title: string; children: ReactNode; className?: string }) {
  return (
    <section className={`athlete-form-section ${className}`.trim()} data-layout="grouped-two-column">
      <h3>{title}</h3>
      <div className="athlete-form-grid">{children}</div>
    </section>
  );
}

function ZoneEditor({ title, zones, unit, onChange }: {
  title: string;
  zones: AthleteProfile["performance"]["powerZones"];
  unit: string;
  onChange: (zones: AthleteProfile["performance"]["powerZones"]) => void;
}) {
  return (
    <fieldset className="athlete-zone-editor athlete-form-span-all">
      <legend>{title}</legend>
      {zones.map((zone, index) => (
        <div className="athlete-zone-row" key={`${zone.name}-${index}`}>
          <label>Zone Name<input value={zone.name} onChange={event => onChange(zones.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item))} /></label>
          <label>{`Minimum (${unit})`}<input type="number" value={zone.minWatts} onChange={event => onChange(zones.map((item, itemIndex) => itemIndex === index ? { ...item, minWatts: Number(event.target.value) } : item))} /></label>
          <label>{`Maximum (${unit})`}<input type="number" value={zone.maxWatts ?? ""} placeholder="No maximum" onChange={event => onChange(zones.map((item, itemIndex) => itemIndex === index ? { ...item, maxWatts: event.target.value ? Number(event.target.value) : null } : item))} /></label>
        </div>
      ))}
    </fieldset>
  );
}

function HeartRateZoneEditor({ zones, onChange }: {
  zones: AthleteProfile["performance"]["heartRateZones"];
  onChange: (zones: AthleteProfile["performance"]["heartRateZones"]) => void;
}) {
  return (
    <fieldset className="athlete-zone-editor athlete-form-span-all">
      <legend>Heart-Rate Zones</legend>
      {zones.length === 0 ? <p className="command-note">No heart-rate zones configured.</p> : null}
      {zones.map((zone, index) => (
        <div className="athlete-zone-row" key={`${zone.name}-${index}`}>
          <label>Zone Name<input value={zone.name} onChange={event => onChange(zones.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item))} /></label>
          <label>Minimum (bpm)<input type="number" value={zone.minBpm} onChange={event => onChange(zones.map((item, itemIndex) => itemIndex === index ? { ...item, minBpm: Number(event.target.value) } : item))} /></label>
          <label>Maximum (bpm)<input type="number" value={zone.maxBpm ?? ""} placeholder="No maximum" onChange={event => onChange(zones.map((item, itemIndex) => itemIndex === index ? { ...item, maxBpm: event.target.value ? Number(event.target.value) : null } : item))} /></label>
        </div>
      ))}
      <button type="button" className="ride-analysis-button" onClick={() => onChange([...zones, { name: `Z${zones.length + 1}`, minBpm: 0, maxBpm: null }])}>Add Heart-Rate Zone</button>
    </fieldset>
  );
}

function ValidationMessage({ message }: { message?: string }) {
  if (!message) {
    return null;
  }

  return <p className="athlete-validation-message">{message}</p>;
}
